/*
 * Quantity Selector (quantity breaks) behavioral JS.
 * Built on Dawn conventions (pubsub, fetchConfig, routes).
 *
 * <qs-breaks>: tier selection -> hidden quantity sync, PDP price updates,
 * per-unit variant selectors, mixed-variant cart adds, per-tier gifts/upsells.
 * <qs-break-upsells>: checkbox toggling + selection state (localStorage).
 */

if (!customElements.get('qs-breaks')) {
  class QsBreaks extends HTMLElement {
    connectedCallback() {
      const payload = this.querySelector('script[type="application/json"]');
      this.variantData = payload ? JSON.parse(payload.textContent) : [];
      this.moneyFormat = this.dataset.moneyFormat;
      this.sectionId = this.dataset.section;
      this.updatePdpPrices = this.dataset.updatePdpPrices === 'true';
      this.updateUnitPrices = this.dataset.updatePrices === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this.preventGalleryScroll = this.dataset.preventGalleryScroll === 'true';
      this.hiddenQty = document.getElementById(`qs-hidden-qty-${this.sectionId}`);
      this.pendingExtrasAdd ??= null;
      this.resubmitting ??= false;

      // --- tier change: sync hidden quantity (no events: product-form.js has
      // its own base*qty ATC handler that would fight the discounted price)
      this.addEventListener('change', this._onChange = (event) => {
        if (event.target.name === 'qs_tier') this.onTierChange(event.target);
        else if (event.target.classList.contains('qs-break__variant-select')) {
          this.onUnitSelectChange(event.target);
        }
      });

      // --- main variant change: pubsub (product-info.js publishes
      // variantChange with {data: {sectionId, variant}}) + document-level
      // fallback for quick-add contexts
      // typeof: PUB_SUB_EVENTS/subscribe are top-level const/function declarations,
      // they are NOT properties of window
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          if (event.data.variant) this.onMainVariantChange(event.data.variant);
        });
      }
      document.addEventListener('change', this._onDocChange = (event) => {
        if (event.target.name !== 'id') return;
        // getAttribute: form.id is clobbered by the variant <input name="id">
        if (event.target.closest('form')?.getAttribute('id') !== `product-form-${this.sectionId}`) return;
        const variant = this.variantData.find((v) => v.id == event.target.value);
        if (variant) this.onMainVariantChange(variant);
      });

      // --- submit interception: gifts/upsells and mixed-variant bundles
      document.addEventListener('submit', this._onSubmit = this.onProductFormSubmit.bind(this), true);

      // --- gift goals: register the rules with the global guard
      // (dd0-gift-guard.js) — the
      // guard enforces removal/qty=1 on every cart change, on any page
      this.registerGiftGoals();

      // --- initial state
      const initInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
      this.currentVariant =
        this.variantData.find((v) => v.id == parseInt(initInput?.value)) || this.variantData[0];
      if (this.currentVariant) this.updateTierPrices(this.currentVariant);
      const checked = this.querySelector('input[name="qs_tier"]:checked');
      if (checked && this.hiddenQty) this.hiddenQty.value = checked.value;
      for (const item of this.unitSelectorItems()) this.updateUnitPickers(item);

      this.classList.add('qs-breaks--motion-ready');
    }

    disconnectedCallback() {
      this.classList.remove('qs-breaks--motion-ready');
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('change', this._onDocChange);
      document.removeEventListener('submit', this._onSubmit, true);
      if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
    }

    // Every gift/upsell slot registers its
    // goal on page load (fallback for legacy lines without the _dd0_* properties)
    registerGiftGoals() {
      if (!window.DD0Gifts) return;
      const seen = new Set();
      this.querySelectorAll('.qs-break__upsell').forEach((node) => {
        if (seen.has(node.dataset.handle)) return;
        seen.add(node.dataset.handle);
        window.DD0Gifts.registerGoal({
          giftHandle: node.dataset.handle,
          giftVariantId: node.dataset.product,
          requiredQuantity: node.dataset.minQuantity,
          mainProduct: this.dataset.productHandle,
          kind: node.dataset.type === 'gift' ? 'gift' : 'upsell',
        });
      });
    }

    /* ------------------------------------------------------------------ */
    /* Tier selection                                                      */
    /* ------------------------------------------------------------------ */

    onTierChange(input) {
      if (this.hiddenQty) this.hiddenQty.value = input.value;
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (this.updatePdpPrices && label?.dataset.calculatedPrice) {
        this.updateMainPrice(
          parseInt(label.dataset.calculatedPrice),
          parseInt(label.dataset.calculatedCompare)
        );
      }
    }

    /* ------------------------------------------------------------------ */
    /* Main variant change                                                 */
    /* ------------------------------------------------------------------ */

    onMainVariantChange(variant) {
      this.currentVariant = this.variantData.find((v) => v.id == variant.id) || variant;
      this.updateTierPrices(this.currentVariant);
      this.resetUnitSelectors(this.currentVariant);
    }

    // Recomputes every tier from the (new) base variant price; per-unit
    // overrides are reset by resetUnitSelectors() right after
    updateTierPrices(variant) {
      const price = variant.price;
      const compareAtPrice = variant.compare_at_price;

      this.querySelectorAll('label[data-quantity]').forEach((label) => {
        const qty = parseInt(label.dataset.quantity);
        const pctLeft = parseFloat(label.dataset.percentageLeft);
        const fixedOff = parseInt(label.dataset.fixedDiscount) || 0;
        const compareMode = label.dataset.compareMode;

        const totalPrice = Math.round(price * qty * pctLeft - fixedOff);
        const baseCompare =
          compareMode === 'compare_price' && compareAtPrice && compareAtPrice > price
            ? compareAtPrice
            : price;
        const comparePrice = baseCompare * qty;

        this.renderTier(label, qty, totalPrice, comparePrice);
      });

      this.refreshSelectedTierPdpPrice();
    }

    renderTier(label, qty, totalPrice, comparePrice) {
      label.dataset.calculatedPrice = totalPrice;
      label.dataset.calculatedCompare = comparePrice;

      const saved = comparePrice - totalPrice;
      const savedRounded = Math.round(saved / 100) * 100;
      const priceEach = Math.round(totalPrice / qty);
      const comparePriceEach = Math.round(comparePrice / qty);
      const percentage =
        comparePrice > 0 && saved > 0 ? Math.floor((saved / comparePrice) * 100) : 0;

      label.querySelectorAll('.variant-price-update[data-text]').forEach((el) => {
        el.innerHTML = this.renderText(el.dataset.text, {
          qty,
          totalPrice,
          comparePrice,
          saved,
          savedRounded,
          priceEach,
          comparePriceEach,
          percentage,
        });
      });

      label
        .querySelectorAll('.qs-break__compare-price')
        .forEach((el) => el.classList.toggle('hidden', comparePrice <= totalPrice));
    }

    refreshSelectedTierPdpPrice() {
      if (!this.updatePdpPrices) return;
      const checkedInput = this.querySelector('input[name="qs_tier"]:checked');
      if (!checkedInput) return;
      const label = this.querySelector(`label[for="${checkedInput.id}"]`);
      if (label?.dataset.calculatedPrice) {
        this.updateMainPrice(
          parseInt(label.dataset.calculatedPrice),
          parseInt(label.dataset.calculatedCompare)
        );
      }
    }

    /* ------------------------------------------------------------------ */
    /* Per-unit variant selectors                                          */
    /* ------------------------------------------------------------------ */

    unitSelectorItems(scope) {
      return Array.from((scope || this).querySelectorAll('.qs-break__selector-item'));
    }

    resetUnitSelectors(variant) {
      this.unitSelectorItems().forEach((item) => {
        item.dataset.selectedId = variant.id;
        const selects = Array.from(item.querySelectorAll('.qs-break__variant-select'));
        selects.forEach((select, index) => {
          const value = variant[`option${index + 1}`];
          if (value != null) select.value = value;
        });
        this.refilterUnit(item, 0);
        this.updateUnitPickers(item);
        this.updateUnitImage(item, variant);
      });
    }

    onUnitSelectChange(select) {
      const item = select.closest('.qs-break__selector-item');
      if (!item) return;
      const selects = Array.from(item.querySelectorAll('.qs-break__variant-select'));
      const changedIndex = selects.indexOf(select);

      this.refilterUnit(item, changedIndex + 1);
      this.updateUnitPickers(item);

      const values = selects.map((s) => s.value);
      const variant = this.variantData.find((v) =>
        values.every((value, index) => v[`option${index + 1}`] === value)
      );
      if (!variant) return;

      item.dataset.selectedId = variant.id;
      selects.forEach((s) => (s.dataset.productId = variant.id));
      this.updateUnitImage(item, variant);

      if (!this.preventGalleryScroll && variant.featured_media?.id) {
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        gallery?.setActiveMedia?.(`${this.sectionId}-${variant.featured_media.id}`, true);
      }

      if (this.updateUnitPrices) this.updateTierFromUnits(select.closest('label[data-quantity]'));
    }

    // Re-derives exists/available for selects after `fromIndex`, given the
    // unit's earlier selections (mirrors the Liquid option chaining)
    refilterUnit(item, fromIndex) {
      const selects = Array.from(item.querySelectorAll('.qs-break__variant-select'));
      for (let position = Math.max(fromIndex, 1); position < selects.length; position++) {
        const select = selects[position];
        const prefix = selects.slice(0, position).map((s) => s.value);
        Array.from(select.options).forEach((option) => {
          const matching = this.variantData.filter(
            (v) =>
              prefix.every((value, index) => v[`option${index + 1}`] === value) &&
              v[`option${position + 1}`] === option.value
          );
          const exists = matching.length > 0;
          const available = matching.some((v) => v.available);
          option.classList.toggle('non-existent', !exists);
          option.classList.toggle('unavailable', exists && !available);
          const hidden = !exists || (!available && this.skipUnavailable);
          option.disabled = hidden;
          option.hidden = hidden;
        });
        const current = select.selectedOptions[0];
        if (current && (current.disabled || current.hidden)) {
          const fallback = Array.from(select.options).find((o) => !o.disabled && !o.hidden);
          if (fallback) select.value = fallback.value;
        }
      }
    }

    updateUnitPickers(item) {
      for (const select of item.querySelectorAll('.qs-break__variant-select')) {
        const label = select.closest('.select').querySelector('.qs-break__select-value');
        if (label) label.textContent = select.selectedOptions[0]?.textContent.trim() || '';
        const color = select.selectedOptions[0]?.dataset.swatchColor;
        if (color && CSS.supports('color', color)) {
          select.closest('.select').style.setProperty('--qs-picker-swatch-color', color);
        }
      }
    }

    updateUnitImage(item, variant) {
      const img = item.querySelector('.qs-break__selector-item__image img');
      if (img && variant.featured_image?.src) {
        const src = variant.featured_image.src;
        // Request a CDN-resized rendition, not the full-size original
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=600`;
        // srcset would override src — drop the Liquid-rendered one on swap
        img.removeAttribute('srcset');
        if (variant.featured_image.alt != null) img.alt = variant.featured_image.alt;
      }
    }

    // Tier price = sum of the unit variants' prices with the tier discount
    updateTierFromUnits(label) {
      if (!label) return;
      const qty = parseInt(label.dataset.quantity);
      const pctLeft = parseFloat(label.dataset.percentageLeft);
      const fixedOff = parseInt(label.dataset.fixedDiscount) || 0;
      const compareMode = label.dataset.compareMode;

      const units = this.unitSelectorItems(label);
      if (!units.length) return;
      let sumPrice = 0;
      let sumCompare = 0;
      units.forEach((item) => {
        const variant =
          this.variantData.find((v) => v.id == item.dataset.selectedId) || this.currentVariant;
        if (!variant) return;
        sumPrice += variant.price;
        const baseCompare =
          compareMode === 'compare_price' &&
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
            ? variant.compare_at_price
            : variant.price;
        sumCompare += baseCompare;
      });

      const totalPrice = Math.round(sumPrice * pctLeft - fixedOff);
      this.renderTier(label, qty, totalPrice, sumCompare);

      const checkedInput = this.querySelector('input[name="qs_tier"]:checked');
      if (checkedInput && label === this.querySelector(`label[for="${checkedInput.id}"]`)) {
        this.refreshSelectedTierPdpPrice();
      }
    }

    /* ------------------------------------------------------------------ */
    /* Cart: submit interception (gifts, upsells, mixed variants)          */
    /* ------------------------------------------------------------------ */

    checkedTierLabel() {
      const checked = this.querySelector('input[name="qs_tier"]:checked');
      return checked ? this.querySelector(`label[for="${checked.id}"]`) : null;
    }

    tierExtras(label) {
      const container = label?.querySelector('qs-break-upsells');
      if (!container) return { gifts: [], upsells: [] };
      const nodes = Array.from(container.querySelectorAll('.qs-break__upsell'));
      return {
        gifts: nodes
          .filter((node) => node.dataset.type === 'gift')
          .map((node) => ({
            id: parseInt(node.dataset.product, 10),
            min: parseInt(node.dataset.minQuantity, 10) || 1,
          }))
          .filter((gift) => gift.id),
        upsells: nodes
          .filter((node) => node.dataset.type === 'upsell' && node.dataset.selected === 'true')
          .map((node) => ({
            id: parseInt(node.dataset.product, 10),
            min: parseInt(node.dataset.minQuantity, 10) || 1,
          }))
          .filter((upsell) => upsell.id),
      };
    }

    // Hidden (_-prefixed) properties: mark the line as a gift/upsell and record
    // which product unlocked it and at which quantity, so the guard can enforce
    // the rules on any cart line regardless of which page/session added it
    giftLineItem(gift) {
      return {
        id: gift.id,
        quantity: 1,
        properties: {
          _dd0_free_gift: true,
          _dd0_gift_parent: this.dataset.productHandle,
          _dd0_gift_min: gift.min,
        },
      };
    }

    upsellLineItem(upsell) {
      return {
        id: upsell.id,
        quantity: 1,
        properties: {
          _dd0_upsell: true,
          _dd0_gift_parent: this.dataset.productHandle,
          _dd0_gift_min: upsell.min,
        },
      };
    }

    // Per-unit selections that differ from the form's variant make the tier a
    // mixed-variant bundle: it can't be submitted as a plain quantity
    tierMixedItems(label) {
      const units = this.unitSelectorItems(label);
      if (!units.length) return null;
      const formVariantId = parseInt(
        document.querySelector(`#product-form-${this.sectionId} input[name="id"]`)?.value,
        10
      );
      const ids = units.map((item) => parseInt(item.dataset.selectedId, 10) || formVariantId);
      if (ids.every((id) => id === formVariantId)) return null;
      const grouped = {};
      ids.forEach((id) => (grouped[id] = (grouped[id] || 0) + 1));
      return Object.entries(grouped).map(([id, quantity]) => ({ id: parseInt(id, 10), quantity }));
    }

    onProductFormSubmit(event) {
      if (this.resubmitting) {
        this.resubmitting = false;
        return;
      }
      // Swallow re-clicks while extras are being added (see v1)
      if (this.pendingExtrasAdd) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const form = event.target;
      // getAttribute: HTMLFormElement is [OverrideBuiltins] — form.id/form.action
      // get clobbered by controls named "id"/"action" (Dawn's variant input)
      if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== `product-form-${this.sectionId}`) return;
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
      const submitButton = form.closest('product-form')?.querySelector('[type="submit"]');
      if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

      const label = this.checkedTierLabel();
      if (!label) return;
      const extras = this.tierExtras(label);
      const mixedItems = this.tierMixedItems(label);
      if (!mixedItems && !extras.gifts.length && !extras.upsells.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitButton) submitButton.classList.add('loading');

      if (mixedItems) {
        this.submitMixedBundle(form, submitButton, mixedItems, extras);
        return;
      }

      // Extras only: add them first (deduped against the real cart), then
      // resubmit so Dawn's product-form handles the main add + sections render
      const submitter = event.submitter;
      this.pendingExtrasAdd = this.addExtras(extras)
        .catch((error) => console.warn('[qs-breaks] extras add error', error))
        .then(() => {
          this.pendingExtrasAdd = null;
          this.resubmitting = true;
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
          else form.submit();
        });
    }

    // Deduplicates against the real cart read at click time (stale-cache
    // lesson from dd0-free-gifts)
    async addExtras(extras) {
      const cart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((response) =>
        response.json()
      );
      const inCart = new Set(cart.items.map((item) => item.variant_id));
      const items = [
        ...extras.gifts.filter((gift) => !inCart.has(gift.id)).map((gift) => this.giftLineItem(gift)),
        ...extras.upsells
          .filter((upsell) => !inCart.has(upsell.id))
          .map((upsell) => this.upsellLineItem(upsell)),
      ];
      if (!items.length) return;

      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items }),
      }).then((response) => response.json());
      // Multi-item adds are all-or-nothing: one sold-out item blocks them all
      if (response.status) {
        console.warn('[qs-breaks] extras not added:', response.description || response.message);
      }
    }

    // Mixed-variant bundles bypass Dawn's product-form (it submits a single
    // variant): add every line ourselves and render the cart sections
    async submitMixedBundle(form, submitButton, mixedItems, extras) {
      this.pendingExtrasAdd = Promise.resolve();
      // Full Dawn loading state: class + aria + spinner
      const spinner = form.closest('product-form')?.querySelector('.loading__spinner');
      if (submitButton) submitButton.setAttribute('aria-disabled', 'true');
      spinner?.classList.remove('hidden');
      try {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const items = [
          ...mixedItems,
          ...extras.gifts.map((gift) => this.giftLineItem(gift)),
          ...extras.upsells.map((upsell) => this.upsellLineItem(upsell)),
        ];
        // Dedupe gifts/upsells (not the bundle lines) against the live cart
        const liveCart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then(
          (response) => response.json()
        );
        const inCart = new Set(liveCart.items.map((item) => item.variant_id));
        const mixedIds = new Set(mixedItems.map((item) => item.id));
        const filtered = items.filter((item) => mixedIds.has(item.id) || !inCart.has(item.id));

        const body = { items: filtered };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        }).then((res) => res.json());

        if (response.status) {
          const productForm = form.closest('product-form');
          if (typeof productForm?.handleErrorMessage === 'function') {
            productForm.handleErrorMessage(response.description || response.message);
          } else {
            console.warn('[qs-breaks] bundle not added:', response.description || response.message);
          }
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mixedItems[0]?.id,
            cartData: response,
          });
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          window.location = routes.cart_url;
        }
      } catch (error) {
        console.warn('[qs-breaks] bundle add error', error);
      } finally {
        this.pendingExtrasAdd = null;
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
        spinner?.classList.add('hidden');
      }
    }

    /* ------------------------------------------------------------------ */
    /* Rendering helpers (same token set as qs-text-with-price.liquid)    */
    /* ------------------------------------------------------------------ */

    renderText(template, { qty, totalPrice, comparePrice, saved, savedRounded, priceEach, comparePriceEach, percentage }) {
      const tokens = {
        '[quantity]': qty,
        '[price]': `<span>${this.formatMoney(totalPrice)}</span>`,
        '[compare_price]': `<span>${this.formatMoney(comparePrice)}</span>`,
        '[amount_saved]': `<span>${this.formatMoney(saved)}</span>`,
        '[amount_saved_rounded]': `<span>${this.formatMoney(savedRounded)}</span>`,
        '[price_each]': `<span>${this.formatMoney(priceEach)}</span>`,
        '[compare_price_each]': `<span>${this.formatMoney(comparePriceEach)}</span>`,
        '[percentage_saved]': percentage > 0 ? `<span>${percentage}%</span>` : '',
      };
      return Object.entries(tokens).reduce((t, [k, v]) => t.replaceAll(k, String(v)), template);
    }

    updateMainPrice(price, comparePrice) {
      const priceBlock = document.getElementById(`price-${this.sectionId}`);
      if (priceBlock) {
        const priceInner = priceBlock.querySelector('.price') || priceBlock;
        const onSale = comparePrice > price;
        priceInner.classList.toggle('price--on-sale', onSale);

        const badge = priceBlock.querySelector('.price__badge-sale');
        if (badge && onSale) {
          const tmpl = badge.dataset.badgeTemplate || '';
          const diff = comparePrice - price;
          const pct = comparePrice > 0 ? Math.floor((diff / comparePrice) * 100) : 0;
          badge.textContent = tmpl
            .replace('[percentage]', pct > 0 ? pct + '%' : '')
            .replace('[amount]', this.formatMoney(diff));
        }

        const salePriceEl = priceBlock.querySelector('.price__sale .price-item--sale');
        if (salePriceEl) salePriceEl.innerHTML = this.formatMoney(price);

        const compareEl = priceBlock.querySelector('.price__sale .price-item--regular');
        if (compareEl) compareEl.innerHTML = this.formatMoney(comparePrice);

        const regularEl = priceBlock.querySelector('.price__regular .price-item--regular');
        if (regularEl) regularEl.innerHTML = this.formatMoney(price);
      }

      const atcBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (atcBtn) {
        const atcPriceEl = atcBtn.querySelector('.atc-price');
        const atcCompareEl = atcBtn.querySelector('.atc-compare');
        if (atcPriceEl) atcPriceEl.innerHTML = this.formatMoney(price);
        if (atcCompareEl) {
          if (comparePrice > price) {
            atcCompareEl.innerHTML = this.formatMoney(comparePrice);
            atcCompareEl.style.display = '';
          } else {
            atcCompareEl.style.display = 'none';
          }
        }
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return this.moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    }
  }

  customElements.define('qs-breaks', QsBreaks);
}

if (!customElements.get('qs-break-upsells')) {
  class QsBreakUpsells extends HTMLElement {
    connectedCallback() {
      this.saveLocalStorage = this.dataset.saveLocalStorage === 'true';
      this.restoreState();
      this.addEventListener('click', this._onClick = (event) => {
        const upsell = event.target.closest('.qs-break__upsell[data-toggle="true"]');
        if (!upsell || !this.contains(upsell)) return;
        // Don't let the click bubble into the surrounding tier <label> twice;
        // toggling must not also re-trigger the radio default behavior
        event.preventDefault();
        this.toggleUpsell(upsell);
      });
      // role="checkbox" keyboard support
      this.addEventListener('keydown', this._onKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const upsell = event.target.closest('.qs-break__upsell[data-toggle="true"]');
        if (!upsell || !this.contains(upsell)) return;
        event.preventDefault();
        this.toggleUpsell(upsell);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
    }

    toggleUpsell(upsell) {
      const selected = upsell.dataset.selected === 'true';
      upsell.dataset.selected = String(!selected);
      upsell.setAttribute('aria-checked', String(!selected));
      this.persistState(upsell);
    }

    storageKey(upsell) {
      return `qs-upsell-${this.dataset.section}-${this.dataset.offer}-${upsell.dataset.handle}`;
    }

    restoreState() {
      if (!this.saveLocalStorage) return;
      this.querySelectorAll('.qs-break__upsell[data-toggle="true"]').forEach((upsell) => {
        try {
          const saved = localStorage.getItem(this.storageKey(upsell));
          if (saved !== null) {
            upsell.dataset.selected = saved;
            upsell.setAttribute('aria-checked', saved);
          }
        } catch (error) {
          /* private mode: ignore */
        }
      });
    }

    persistState(upsell) {
      if (!this.saveLocalStorage) return;
      try {
        localStorage.setItem(this.storageKey(upsell), upsell.dataset.selected);
      } catch (error) {
        /* private mode: ignore */
      }
    }
  }

  customElements.define('qs-break-upsells', QsBreakUpsells);
}
